import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiHome, FiFileText } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const howItWorks = [
  {
    step: 1,
    title: "Land purchase",
    description:
      "If you're buying land separately, we arrange finance for the land purchase first. You start paying interest on this amount.",
  },
  {
    step: 2,
    title: "Loan approval",
    description:
      "We get your construction loan approved based on your builder's fixed price contract and plans. The full amount is approved but not drawn yet.",
  },
  {
    step: 3,
    title: "Progress payments",
    description:
      "As construction progresses, your builder invoices for completed stages. We arrange payments and you only pay interest on drawn funds.",
  },
  {
    step: 4,
    title: "Completion",
    description:
      "Once construction is complete and you have your certificate of occupancy, the loan converts to a standard home loan.",
  },
];

const stages = [
  { name: "Deposit", typical: "5%" },
  { name: "Base/Slab", typical: "15%" },
  { name: "Frame", typical: "20%" },
  { name: "Lock-up", typical: "20%" },
  { name: "Fit-out", typical: "25%" },
  { name: "Completion", typical: "15%" },
];

const requirements = [
  {
    title: "Fixed price building contract",
    description:
      "Most lenders require a fixed price contract from a licensed builder. This protects you and the lender from cost overruns.",
  },
  {
    title: "Council-approved plans",
    description:
      "Your building plans must be approved by the local council before the lender will release funds.",
  },
  {
    title: "Builder's insurance",
    description:
      "Your builder must have appropriate insurance (Home Building Compensation Fund or equivalent).",
  },
  {
    title: "Adequate deposit",
    description:
      "Typically 10-20% of the total cost (land + construction). Can sometimes use equity in existing property.",
  },
];

const tips = [
  {
    title: "Get your finance approved first",
    description:
      "Construction loans have stricter requirements. Get pre-approval before signing a building contract.",
  },
  {
    title: "Include a contingency buffer",
    description:
      "Even with a fixed price contract, allow 5-10% for variations, upgrades, and unexpected costs.",
  },
  {
    title: "Understand progress payments",
    description:
      "Know the payment schedule and ensure you can cover any gaps between drawdowns and builder invoices.",
  },
  {
    title: "Check builder credentials",
    description:
      "Ensure your builder is licensed, insured, and has a good track record. Lenders may have builder requirements.",
  },
  {
    title: "Lock in your rate if possible",
    description:
      "Construction can take 6-12+ months. Consider locking in your rate to protect against rises.",
  },
  {
    title: "Plan for the interest-only period",
    description:
      "You'll pay interest on drawn funds during construction. Budget for potentially paying rent/mortgage plus construction interest.",
  },
];

export function ConstructionLoansPage() {
  return (
    <>
      <SEO
        title="Construction Loans Brisbane, Springfield & Ipswich"
        description="Construction loans for building in Brisbane, Springfield, Logan & Ipswich. Progressive drawdowns, land-and-build packages, knock-down rebuild finance."
        url="/home-loans/construction"
        keywords={[
          "construction loan Brisbane",
          "construction loan Springfield",
          "land and build Brisbane",
          "knock down rebuild loan",
          "owner builder loan",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Construction Loans",
            description:
              "Construction home loans with progressive drawdowns, land-and-build packages, knock-down rebuild and owner-builder options.",
            url: "/home-loans/construction",
            serviceType: "Construction Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "Construction", url: "/home-loans/construction" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/home-loans"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <FiArrowRight className="size-4 mr-2 rotate-180" />
              Home Loans
            </Link>
            <p className="section-label mb-4">Construction Loans</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Build your dream home
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Construction loans work differently to regular home loans. Instead
              of one lump sum, funds are released in stages as your build
              progresses. We'll guide you through the process and manage the
              drawdowns.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Discuss Your Build
                <FiArrowRight className="size-4" />
              </Link>
              <Link
                to="/calculators/borrowing-power"
                className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
              >
                Check Borrowing Power
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How construction loans work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Unlike a standard home loan, construction loans release funds in
              stages as your build progresses.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item) => (
              <div key={item.step} className="bg-muted/30 rounded-2xl p-6">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Progress payments */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Progress Payments</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Typical drawdown stages
              </h2>
              <p className="text-muted-foreground mb-6">
                Your builder will invoice at each stage of construction. These
                percentages are typical but can vary based on your contract.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> You only pay interest on funds that have
                been drawn down, not the full loan amount.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-border/50">
              <div className="space-y-4">
                {stages.map((stage, index) => (
                  <div key={stage.name} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium">{stage.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-primary">
                        {stage.typical}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total</span>
                  <span className="font-semibold text-primary">100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Requirements</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              What you'll need
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {requirements.map((req) => (
              <div
                key={req.title}
                className="flex items-start gap-4 p-6 bg-muted/30 rounded-2xl"
              >
                <div className="p-3 bg-white rounded-xl text-primary flex-shrink-0">
                  <FiFileText className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{req.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {req.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">
              Tips
            </p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Construction loan tips
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tips.map((tip, index) => (
              <div key={tip.title} className="bg-white/10 rounded-2xl p-6">
                <div className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-medium mb-4">
                  {index + 1}
                </div>
                <h3 className="font-semibold mb-2">{tip.title}</h3>
                <p className="text-sm text-primary-foreground/80">
                  {tip.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Land & Build */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Options</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Land & build packages
              </h2>
              <p className="text-muted-foreground mb-6">
                Many builders offer combined land and build packages which can
                simplify the process. We can arrange finance for:
              </p>
              <ul className="space-y-3">
                {[
                  "House and land packages from project home builders",
                  "Separate land purchase followed by construction",
                  "Knockdown rebuild on existing land",
                  "Custom builds with your own architect and builder",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FiCheck className="size-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-cream rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <FiHome className="size-6 text-primary" />
                <h3 className="font-semibold text-lg">Need land first?</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                We can help you secure finance for the land while you finalise
                your building plans. The construction loan is then added once
                your plans and contract are ready.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                Discuss your options
                <FiArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to build?"
        title="Let's discuss your construction project"
        description="Book a free consultation and we'll guide you through construction finance."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
